import { sampleValueFor } from '../../utils/displaySample';
import { visibleColumns } from '../../utils/repeatingGroupEntries';

/**
 * The record the page designer draws on a FORM (KPR-1861, KPE-041).
 *
 * The designer has no record, so a form's blocks were blank outlines over the
 * paper: nothing to see where a value would land, and nothing to measure against
 * the box. On a form the editor now draws a sample value in each block, the same
 * made-up value the template editor and the card home preview with
 * (utils/displaySample.js), and tells the person when a sample does not fit.
 *
 * Only the types whose sample has the element's own shape get one. A choice, a
 * lookup, a person, a file, a web address, a "me too" count and a repeating group
 * have a value that is an id, a list or something the real renderer checks, where
 * the sample machinery would hand back the element's name and the renderer would
 * draw nonsense or fail. Those blocks stay blank
 * here, and the print preview, which has a real record, is where they are checked.
 *
 * A fixed instant, not the clock: the sample is the same on every open, and a
 * date is measured by its width, which midnight in another zone does not change.
 */
export const SAMPLE_INSTANT = '2026-09-09T14:30:00.000Z';

const SAMPLED = new Set([
	'text-small', 'text-large', 'rich-text',
	'number', 'decimal', 'integer', 'measurement', 'currency', 'rating', 'boolean',
	'phone', 'email', 'location', 'color',
	'date', 'date-time', 'time',
]);

/**
 * `count` entries of a group, each holding its columns' samples: what a group block
 * draws on every line in the designer (KPR-1863), so the person sees where the text
 * sits. Not in `formSampleItem`, where a block showing the group as one value would
 * read it. One object, because the lines are told apart by their place.
 */
export function sampleEntries(group, count) {
	const elements = {};
	for (const column of visibleColumns(group)) {
		if (!SAMPLED.has(column.dataType)) continue;
		const value = sampleValueFor(column, SAMPLE_INSTANT);
		if (value !== '' && value !== null && value !== undefined) elements[column.name] = value;
	}
	return Array(Math.max(0, count) || 0).fill({ id: 'sample', elements });
}

export function formSampleItem(elementDefs = []) {
	const elements = {};
	for (const def of elementDefs || []) {
		if (!def?.name || !SAMPLED.has(def.dataType)) continue;
		const value = sampleValueFor(def, SAMPLE_INSTANT);
		if (value !== '' && value !== null && value !== undefined) elements[def.name] = value;
	}
	return { _id: 'sample', displayValue: '', elements };
}
